import React, { useState, useEffect, useLayoutEffect } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { auth, db } from '../firebase';
import {
  collection,
  query,
  where,
  getDocs,
  orderBy,
  deleteDoc,
  doc,
  getDoc,
  addDoc,
  updateDoc,
  serverTimestamp,
  arrayUnion,
  arrayRemove,
} from 'firebase/firestore';
import {
  fetchOwnRunsForUserFirestore,
  sortRunsByActivityDesc,
  firestoreDocWithId,
} from '../utils/fetchOwnRunsFromFirestore';
import { getFunctions, httpsCallable } from 'firebase/functions';
import RunCard from './RunCard';
import { FaPlus, FaSearch, FaFilter, FaSortAmountDown, FaThLarge, FaThList, FaCheckSquare, FaSquare, FaChartLine, FaCog } from 'react-icons/fa';
import { getViewableRunUserIds } from '../utils/userGroups';
import { cacheKey, getCachedStale, getCachedStaleAsync } from '../utils/pageCache';
import {
  setMyRidesCacheWithFullList,
  clearMyRidesCaches,
  loadMyRunsFullListStaleAsync,
  myRunsFullListCacheKey,
} from '../utils/myRunsFullListCache';
import {
  RUN_CACHE_PATCHED_EVENT,
  salvageCachedRunsNotInFetch,
  removeRunIdFromUserRunListCaches,
  restoreRunToUserRunListCaches,
} from '../utils/runListCache';
import {
  listPendingRunsForDisplay,
  discardLocalPendingRun,
  removePendingQueueEntryByClientKey,
  mergeFirestoreRunsWithPending,
} from '../utils/pendingRunsQueue';
import './MyRides.css';
import RunGroupPickerModal from './RunGroupPickerModal';
import CreateRunGroupModal from './CreateRunGroupModal';
import ManageRunGroupsModal from './ManageRunGroupsModal';
import ModalPopup from './ModalPopup';
import { ownsRunFirestoreDoc } from '../utils/runAccess';

const DELETE_RUN_MODAL_TITLE = 'Delete this run?';
const DELETE_RUN_MODAL_MESSAGE =
  'This removes the run from the cloud and your list. This cannot be undone.';

function mergeCachedRunsForSalvage(myRidesKey, userId) {
  const fromMy = getCachedStale(myRidesKey)?.runs ?? [];
  const fromFull = getCachedStale(myRunsFullListCacheKey(userId))?.runs ?? [];
  const idSeen = new Set();
  const out = [];
  for (const r of [...fromMy, ...fromFull]) {
    if (!r?.id || idSeen.has(r.id)) continue;
    idSeen.add(r.id);
    out.push(r);
  }
  return out;
}

const MyRides = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const user = auth.currentUser;
  
  const [runs, setRuns] = useState([]);
  const [filteredRuns, setFilteredRuns] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState('');
  const [sortBy, setSortBy] = useState('date-desc'); // date-desc, date-asc, name, duration-*, distance-*, tss-*
  const [filterBy, setFilterBy] = useState('all'); // all, with-video, with-telemetry
  const [viewMode, setViewMode] = useState(() => {
    try {
      return window?.localStorage?.getItem('myRunsViewMode') || 'grid';
    } catch (e) {
      return 'grid';
    }
  }); // grid | list
  const [selectMode, setSelectMode] = useState(false);
  const [selectedRunIds, setSelectedRunIds] = useState(new Set());
  const [groups, setGroups] = useState([]);
  const [selectedGroupId, setSelectedGroupId] = useState('all');
  const [groupPickerRun, setGroupPickerRun] = useState(null);
  const [showCreateGroup, setShowCreateGroup] = useState(false);
  const [showManageGroups, setShowManageGroups] = useState(false);
  const [groupBusy, setGroupBusy] = useState(false);
  const [stravaStatus, setStravaStatus] = useState({ connected: false });
  const [brakeaceStatus, setBrakeaceStatus] = useState({ connected: false });
  const [stravaSyncing, setStravaSyncing] = useState(false);
  const [refreshing, setRefreshing] = useState(false);
  const [deleteFeedback, setDeleteFeedback] = useState(null);
  const [runPendingDelete, setRunPendingDelete] = useState(null);

  useLayoutEffect(() => {
    const msg = location.state?.runDeletedMessage;
    if (typeof msg !== 'string' || !msg.trim()) return;
    setDeleteFeedback({ variant: 'success', message: msg.trim() });
    navigate(location.pathname, { replace: true, state: {} });
  }, [location.state, location.pathname, navigate]);

  useEffect(() => {
    if (!deleteFeedback) return undefined;
    const t = window.setTimeout(() => setDeleteFeedback(null), 6000);
    return () => window.clearTimeout(t);
  }, [deleteFeedback]);

  useEffect(() => {
    if (!user) {
      navigate('/login');
      return undefined;
    }
    const key = cacheKey('myRides', user.uid);
    const homeKey = cacheKey('home', user.uid);
    let cancelled = false;

    const hydrateRunsFromCache = (runsList, groupsList) => {
      if (!Array.isArray(runsList)) return;
      if (cancelled) return;
      setRuns(runsList);
      setGroups(Array.isArray(groupsList) ? groupsList : []);
      setLoading(false);
      listPendingRunsForDisplay(user.uid).then((pending) => {
        if (cancelled) return;
        setRuns((prev) => {
          const merged = mergeFirestoreRunsWithPending(prev, pending);
          try {
            setMyRidesCacheWithFullList(user.uid, {
              runs: merged,
              groups: Array.isArray(groupsList) ? groupsList : [],
            });
          } catch (_e) {
            // ignore cache write errors
          }
          return merged;
        });
      });
    };

    (async () => {
      let hit = false;
      const fullList = await loadMyRunsFullListStaleAsync(user.uid);
      if (!cancelled && fullList && Array.isArray(fullList.runs) && fullList.runs.length > 0) {
        hydrateRunsFromCache(fullList.runs, fullList.groups);
        hit = true;
      }
      if (!cancelled && !hit) {
        const myLs = getCachedStale(key);
        if (myLs && Array.isArray(myLs.runs) && myLs.runs.length > 0) {
          hydrateRunsFromCache(myLs.runs, myLs.groups);
          hit = true;
        }
      }
      if (!cancelled && !hit) {
        const myIdb = await getCachedStaleAsync(key);
        if (myIdb && Array.isArray(myIdb.runs) && myIdb.runs.length > 0) {
          hydrateRunsFromCache(myIdb.runs, myIdb.groups);
          hit = true;
        }
      }
      if (!cancelled && !hit) {
        const homeStale = getCachedStale(homeKey);
        const recent = homeStale?.stats?.recentRides;
        if (Array.isArray(recent) && recent.length > 0) {
          hydrateRunsFromCache(recent, []);
        } else {
          setLoading(true);
        }
      }

      if (cancelled) return;
      setRefreshing(true);
      Promise.all([
        fetchRuns().catch((err) => {
          console.error('Error loading runs from cloud:', err);
          return [];
        }),
        fetchGroups().catch((err) => {
          console.error('Error loading groups:', err);
          return [];
        }),
        listPendingRunsForDisplay(user.uid),
      ])
        .then(([runsFromCloud, groups, pendingRuns]) => {
          if (cancelled) return;
          const prevRuns = mergeCachedRunsForSalvage(key, user.uid);
          const homeRecent = getCachedStale(homeKey)?.stats?.recentRides ?? [];
          let runs = mergeFirestoreRunsWithPending(runsFromCloud, pendingRuns);
          runs = salvageCachedRunsNotInFetch(prevRuns, runs);
          runs = salvageCachedRunsNotInFetch(homeRecent, runs);
          const payload = { runs, groups };
          setMyRidesCacheWithFullList(user.uid, payload);
          setRuns(runs);
          setGroups(groups);
        })
        .catch((err) => {
          console.error('Error loading runs/groups:', err);
          alert('Failed to load runs. Please check the console for details.');
        })
        .finally(() => {
          if (cancelled) return;
          setLoading(false);
          setRefreshing(false);
        });
    })();

    if (location.state?.files && location.state?.mode === 'create') {
      console.log('Files to upload:', location.state.files);
    }

    return () => {
      cancelled = true;
    };
  }, [user, navigate, location.key]);

  useEffect(() => {
    if (!user) return undefined;
    const key = cacheKey('myRides', user.uid);
    const homeKey = cacheKey('home', user.uid);
    const refresh = () => {
      Promise.all([
        fetchRuns().catch(() => []),
        fetchGroups().catch(() => []),
        listPendingRunsForDisplay(user.uid),
      ]).then(([runsFromCloud, groups, pending]) => {
        const prevRuns = mergeCachedRunsForSalvage(key, user.uid);
        const homeRecent = getCachedStale(homeKey)?.stats?.recentRides ?? [];
        let merged = mergeFirestoreRunsWithPending(runsFromCloud, pending);
        merged = salvageCachedRunsNotInFetch(prevRuns, merged);
        merged = salvageCachedRunsNotInFetch(homeRecent, merged);
        setRuns(merged);
        setGroups(groups);
        setMyRidesCacheWithFullList(user.uid, { runs: merged, groups });
      });
    };
    const onCachePatched = (e) => {
      if (e.detail?.userId !== user.uid) return;
      const stale = getCachedStale(key);
      if (stale?.runs) setRuns(stale.runs);
      if (stale?.groups) setGroups(stale.groups);
    };
    window.addEventListener('online', refresh);
    window.addEventListener(RUN_CACHE_PATCHED_EVENT, onCachePatched);
    return () => {
      window.removeEventListener('online', refresh);
      window.removeEventListener(RUN_CACHE_PATCHED_EVENT, onCachePatched);
    };
  }, [user]);

  useEffect(() => {
    try {
      window?.localStorage?.setItem('myRunsViewMode', viewMode);
    } catch (e) {
      // ignore
    }
  }, [viewMode]);

  useEffect(() => {
    filterAndSortRuns();
  }, [runs, searchTerm, sortBy, filterBy]);

  useEffect(() => {
    filterAndSortRuns();
  }, [selectedGroupId]);

  useEffect(() => {
    if (!user) return;
    let cancelled = false;
    (async () => {
      try {
        const [stravaSnap, brakeaceSnap] = await Promise.all([
          getDoc(doc(db, 'stravaTokens', user.uid)),
          getDoc(doc(db, 'brakeaceTokens', user.uid)),
        ]);
        if (!cancelled) {
          setStravaStatus({ connected: !!stravaSnap?.exists?.() });
          setBrakeaceStatus({ connected: !!brakeaceSnap?.exists?.() });
        }
      } catch {
        if (!cancelled) {
          setStravaStatus({ connected: false });
          setBrakeaceStatus({ connected: false });
        }
      }
    })();
    return () => { cancelled = true; };
  }, [user]);

  const fetchGroups = async () => {
    if (!user) return [];
    try {
      const ref = collection(db, 'runGroups');
      try {
        const q = query(ref, where('userId', '==', user.uid), orderBy('name', 'asc'));
        const snap = await getDocs(q);
        return snap.docs.map((d) => firestoreDocWithId(d));
      } catch {
        const q = query(ref, where('userId', '==', user.uid));
        const snap = await getDocs(q);
        return snap.docs.map((d) => firestoreDocWithId(d));
      }
    } catch (error) {
      console.error('Error loading groups:', error);
      return [];
    }
  };

  const loadGroups = async () => {
    const groups = await fetchGroups();
    setGroups(groups);
  };

  const fetchRuns = async () => {
    if (!user) return [];
    try {
      const allRuns = [];
      const ownSorted = await fetchOwnRunsForUserFirestore(db, user.uid);
      allRuns.push(...ownSorted);

      let viewableIds = [user.uid];
      try {
        viewableIds = await getViewableRunUserIds(user.uid);
      } catch (teamErr) {
        console.warn('Could not load team/group visibility (showing your runs only):', teamErr?.code || teamErr?.message);
      }
      const memberIds = viewableIds.filter((id) => id !== user.uid);
      for (const memberId of memberIds) {
        try {
          const memberRuns = await fetchOwnRunsForUserFirestore(db, memberId);
          allRuns.push(...memberRuns);
        } catch (memberErr) {
          console.warn('Could not load runs for member:', memberId, memberErr?.code || memberErr?.message);
        }
      }
      const byId = new Map();
      for (const r of allRuns) {
        if (r?.id) byId.set(r.id, r);
      }
      return sortRunsByActivityDesc(Array.from(byId.values()));
    } catch (error) {
      console.error('Error loading runs:', error);
      throw error;
    }
  };

  const filterAndSortRuns = () => {
    let filtered = [...runs];

    const getValidNumber = (v) => {
      const n = typeof v === 'string' ? Number(v) : v;
      return typeof n === 'number' && Number.isFinite(n) ? n : null;
    };

    const getComparableMillis = (v) => {
      if (v == null) return 0;
      if (typeof v === 'number' && Number.isFinite(v)) return v < 1e12 ? v * 1000 : v;
      if (typeof v === 'string') {
        const ms = Date.parse(v);
        return Number.isFinite(ms) ? ms : 0;
      }
      if (typeof v === 'object') {
        if (typeof v.toMillis === 'function') {
          const ms = v.toMillis();
          return typeof ms === 'number' && Number.isFinite(ms) ? ms : 0;
        }
        if (typeof v.toDate === 'function') {
          const d = v.toDate();
          const ms = d?.getTime?.();
          return typeof ms === 'number' && Number.isFinite(ms) ? ms : 0;
        }
        if (typeof v.seconds === 'number' && Number.isFinite(v.seconds)) return v.seconds * 1000;
      }
      return 0;
    };

    const getDurationSec = (run) =>
      getValidNumber(run?.durationSec ?? run?.strava?.movingTimeSeconds) ?? 0;

    const getDistanceMeters = (run) =>
      getValidNumber(run?.distance ?? run?.strava?.distanceMeters) ?? 0;

    const getTss = (run) => getValidNumber(run?.tss) ?? 0;

    // Apply group filter
    if (selectedGroupId && selectedGroupId !== 'all') {
      filtered = filtered.filter(run => (run.groupIds || []).includes(selectedGroupId));
    }

    // Apply search filter
    if (searchTerm) {
      filtered = filtered.filter(run => 
        (run.name || run.runName || '').toLowerCase().includes(searchTerm.toLowerCase()) ||
        (run.description || '').toLowerCase().includes(searchTerm.toLowerCase())
      );
    }

    // Apply type filter
    if (filterBy !== 'all') {
      if (filterBy === 'with-video') {
        filtered = filtered.filter(
          (run) =>
            run.videoUrl ||
            run.youtubeUrl ||
            run.videos?.length > 0 ||
            (run.pendingSync && run.videoFileName),
        );
      } else if (filterBy === 'with-telemetry') {
        filtered = filtered.filter(
          (run) =>
            (run.telemetryData?.length > 0) ||
            (typeof run.pendingTelemetryCount === 'number' && run.pendingTelemetryCount > 0) ||
            run.hasTelemetryData === true,
        );
      }
    }

    // Apply sorting (use activity date: timestamp || date, then createdAt)
    const getSortDate = (r) => r.timestamp || r.date || r.createdAt;
    filtered.sort((a, b) => {
      switch (sortBy) {
        case 'date-desc':
          return getComparableMillis(getSortDate(b)) - getComparableMillis(getSortDate(a));
        case 'date-asc':
          return getComparableMillis(getSortDate(a)) - getComparableMillis(getSortDate(b));
        case 'name':
          return (a.name || a.runName || '').localeCompare(b.name || b.runName || '');
        case 'duration-desc':
          return getDurationSec(b) - getDurationSec(a);
        case 'duration-asc':
          return getDurationSec(a) - getDurationSec(b);
        case 'distance-desc':
          return getDistanceMeters(b) - getDistanceMeters(a);
        case 'distance-asc':
          return getDistanceMeters(a) - getDistanceMeters(b);
        case 'tss-desc':
          return getTss(b) - getTss(a);
        case 'tss-asc':
          return getTss(a) - getTss(b);
        default:
          return 0;
      }
    });

    setFilteredRuns(filtered);
  };

  const getGroupCounts = () => {
    const counts = {};
    for (const g of groups) counts[g.id] = 0;
    for (const r of runs) {
      const ids = r.groupIds || [];
      for (const id of ids) {
        if (counts[id] !== undefined) counts[id] += 1;
      }
    }
    return counts;
  };

  const groupCounts = getGroupCounts();

  const handleCreateGroup = async ({ name, notes }) => {
    if (!user) return false;
    const trimmed = String(name || '').trim();
    const trimmedNotes = String(notes || '').trim();
    if (!trimmed) return false;
    try {
      setGroupBusy(true);
      const docRef = await addDoc(collection(db, 'runGroups'), {
        userId: user.uid,
        name: trimmed,
        notes: trimmedNotes,
        createdAt: serverTimestamp(),
        updatedAt: serverTimestamp(),
      });
      await loadGroups();
      return docRef.id;
    } catch (error) {
      console.error('Failed to create group:', error);
      const message =
        (error && typeof error === 'object' && 'message' in error && error.message)
          ? error.message
          : String(error);
      alert(`Failed to create group: ${message}`);
      return false;
    } finally {
      setGroupBusy(false);
    }
  };

  const handleToggleRunGroup = async (runId, groupId, shouldAdd) => {
    if (
      typeof runId === 'string' &&
      (runId.startsWith('local-') || runId.startsWith('queued-inline-'))
    ) {
      return;
    }
    try {
      setGroupBusy(true);
      await updateDoc(doc(db, 'runs', runId), {
        groupIds: shouldAdd ? arrayUnion(groupId) : arrayRemove(groupId),
      });
      setRuns(prev =>
        prev.map(r => {
          if (r.id !== runId) return r;
          const current = Array.isArray(r.groupIds) ? r.groupIds : [];
          const next = shouldAdd
            ? Array.from(new Set([...current, groupId]))
            : current.filter(id => id !== groupId);
          return { ...r, groupIds: next };
        })
      );
    } catch (error) {
      console.error('Failed to update run groups:', error);
      alert('Failed to update run groups.');
    } finally {
      setGroupBusy(false);
    }
  };

  const handleUpdateGroupName = async (groupId, newName) => {
    if (!user) return false;
    const trimmed = String(newName || '').trim();
    if (!trimmed) return false;
    try {
      setGroupBusy(true);
      await updateDoc(doc(db, 'runGroups', groupId), {
        name: trimmed,
        updatedAt: serverTimestamp(),
      });
      await loadGroups();
      return true;
    } catch (error) {
      console.error('Failed to update group name:', error);
      alert('Failed to update group name: ' + (error?.message || error));
      return false;
    } finally {
      setGroupBusy(false);
    }
  };

  const handleDeleteGroup = async (groupId) => {
    if (!user) return false;
    try {
      setGroupBusy(true);
      const runIdsWithGroup = runs
        .filter(r => Array.isArray(r.groupIds) && r.groupIds.includes(groupId))
        .map(r => r.id);
      for (const runId of runIdsWithGroup) {
        await updateDoc(doc(db, 'runs', runId), {
          groupIds: arrayRemove(groupId),
        });
      }
      await deleteDoc(doc(db, 'runGroups', groupId));
      await loadGroups();
      setRuns(prev =>
        prev.map(r => {
          if (!Array.isArray(r.groupIds) || !r.groupIds.includes(groupId)) return r;
          return { ...r, groupIds: r.groupIds.filter(id => id !== groupId) };
        })
      );
      return true;
    } catch (error) {
      console.error('Failed to delete group:', error);
      alert('Failed to delete group: ' + (error?.message || error));
      return false;
    } finally {
      setGroupBusy(false);
    }
  };

  const handleRunClick = (runId) => {
    if (selectMode) {
      setSelectedRunIds((prev) => {
        const next = new Set(prev);
        if (next.has(runId)) next.delete(runId);
        else next.add(runId);
        return next;
      });
      return;
    }
    navigate(`/ride/${runId}`);
  };

  const toggleSelectMode = () => {
    setSelectMode((prev) => !prev);
    if (!selectMode) setSelectedRunIds(new Set());
  };

  const handleDeleteRun = async (run) => {
    const runId = run?.id;
    if (!runId) return;
    if (!user?.uid) return;

    const dropFromUiAndCaches = () => {
      removeRunIdFromUserRunListCaches(user.uid, runId);
      setRuns((prev) => prev.filter((r) => r.id !== runId));
    };

    const rollbackList = () => {
      if (!run) return;
      setRuns((prev) => mergeFirestoreRunsWithPending(prev, [run]));
      restoreRunToUserRunListCaches(user.uid, run);
    };

    if (typeof runId === 'string' && runId.startsWith('local-')) {
      dropFromUiAndCaches();
      try {
        await discardLocalPendingRun(runId.slice('local-'.length));
        setDeleteFeedback({ variant: 'success', message: 'Run removed from this device.' });
      } catch (error) {
        console.error('Error deleting local pending run:', error);
        rollbackList();
        setDeleteFeedback({
          variant: 'error',
          message: 'Could not remove that run from this device. It was put back in your list.',
        });
      }
      return;
    }

    if (typeof runId === 'string' && runId.startsWith('queued-inline-')) {
      dropFromUiAndCaches();
      const clientKey = runId.slice('queued-inline-'.length);
      try {
        removePendingQueueEntryByClientKey(clientKey);
        setDeleteFeedback({ variant: 'success', message: 'Queued run removed from this device.' });
      } catch (error) {
        console.error('Error deleting queued run:', error);
        rollbackList();
        setDeleteFeedback({
          variant: 'error',
          message: 'Could not remove that queued run. It was put back in your list.',
        });
      }
      return;
    }

    // Cloud id: delete on server first, then drop from UI. Removing the row before deleteDoc
    // lets background refetches merge the run back in while the doc still exists.
    try {
      if (auth.currentUser) {
        try {
          await auth.currentUser.getIdToken(true);
        } catch (tokenErr) {
          console.warn('Auth token before delete run:', tokenErr);
        }
      }
      const runRef = doc(db, 'runs', runId);
      let snap;
      try {
        snap = await getDoc(runRef);
      } catch (readErr) {
        console.warn('[MyRides] getDoc before delete failed:', runId, readErr?.code || readErr);
        setDeleteFeedback({
          variant: 'error',
          message: 'Could not reach the server. The run is still in your list — check your connection and try again.',
        });
        return;
      }
      if (snap.exists() && ownsRunFirestoreDoc(user.uid, snap.data())) {
        try {
          await deleteDoc(snap.ref);
        } catch (delErr) {
          console.warn('[MyRides] deleteDoc failed:', runId, delErr?.code || delErr);
          setDeleteFeedback({
            variant: 'error',
            message: 'Could not delete this run on the server. It stays in your list until delete succeeds.',
          });
          return;
        }
        dropFromUiAndCaches();
        setDeleteFeedback({
          variant: 'success',
          message: 'Run deleted from the cloud and removed from your list.',
        });
        return;
      }
      if (snap.exists()) {
        console.warn('[MyRides] run doc exists but is not owned by this user; stale list row:', runId);
        dropFromUiAndCaches();
        setDeleteFeedback({
          variant: 'success',
          message: 'Removed from your list. (This run was not yours to delete on the server.)',
        });
        return;
      }
      console.warn('[MyRides] no Firestore doc for id; removing list row:', runId);
      dropFromUiAndCaches();
      setDeleteFeedback({
        variant: 'success',
        message: 'Removed from your list. (It was already gone on the server.)',
      });
    } catch (error) {
      console.error('Error deleting run:', error);
      setDeleteFeedback({
        variant: 'error',
        message: 'Something went wrong while deleting. The run is still in your list.',
      });
    }
  };

  const handleCreateNew = () => {
    navigate('/ride/create');
  };

  const handleSyncStrava = async () => {
    if (!user || !stravaStatus.connected || stravaSyncing) return;
    setStravaSyncing(true);
    try {
      const fn = httpsCallable(getFunctions(), 'stravaSyncActivities');
      await fn({ rangeDays: 90 });
      clearMyRidesCaches(user.uid);
      const [runsData, groupsData, pendingRuns] = await Promise.all([
        fetchRuns(),
        fetchGroups(),
        listPendingRunsForDisplay(user.uid),
      ]);
      const merged = mergeFirestoreRunsWithPending(runsData, pendingRuns);
      setRuns(merged);
      setGroups(groupsData);
      setMyRidesCacheWithFullList(user.uid, { runs: merged, groups: groupsData });
    } catch (err) {
      console.error('Strava sync failed:', err);
      alert(err?.message || 'Failed to sync with Strava. Try again.');
    } finally {
      setStravaSyncing(false);
    }
  };

  const handleSyncBrakeace = () => {
    navigate('/ride/create', { state: { openImport: 'brakeace' } });
  };

  return (
    <div className="my-rides-container">
      {/* Header */}
      <div className="my-rides-header">
        <div>
          <div className="page-title-with-spinner">
            <h1>My Runs</h1>
            {(loading || refreshing) && <span className="page-title-spinner" aria-hidden="true" title={refreshing ? 'Checking for updates…' : 'Loading…'} />}
          </div>
          <p className="subtitle">
            {loading
              ? 'Loading your runs…'
              : refreshing
                ? 'Checking for updates…'
                : `${runs.length} run${runs.length !== 1 ? 's' : ''} in total${filteredRuns.length !== runs.length ? ` (${filteredRuns.length} shown)` : ''}`}
          </p>
        </div>
        <div className="my-rides-header-actions">
          {stravaStatus.connected && (
            <button
              type="button"
              className="sync-secondary-button"
              onClick={handleSyncStrava}
              disabled={loading || stravaSyncing}
            >
              {stravaSyncing ? 'Syncing…' : 'Sync with Strava'}
            </button>
          )}
          {brakeaceStatus.connected && (
            <button
              type="button"
              className="sync-secondary-button"
              onClick={handleSyncBrakeace}
              disabled={loading}
            >
              Sync with BrakeAce
            </button>
          )}
          <button className="create-ride-button" onClick={handleCreateNew} disabled={loading}>
            <FaPlus /> New Run
          </button>
        </div>
      </div>

      {deleteFeedback && (
        <div
          className={`my-rides-delete-feedback my-rides-delete-feedback--${deleteFeedback.variant}`}
          role="status"
        >
          {deleteFeedback.message}
        </div>
      )}

      {loading ? (
        <div className="loading-spinner" style={{ minHeight: 120 }}>
          <div>Loading your runs…</div>
        </div>
      ) : (
        <>
      {/* Groups row */}
      <div className="run-groups-row">
        <button
          type="button"
          className={`run-group-chip ${selectedGroupId === 'all' ? 'active' : ''}`}
          onClick={() => setSelectedGroupId('all')}
          onDragOver={(e) => e.preventDefault()}
        >
          All ({runs.length})
        </button>
        {groups.map((g) => (
          <button
            key={g.id}
            type="button"
            className={`run-group-chip ${selectedGroupId === g.id ? 'active' : ''}`}
            onClick={() => setSelectedGroupId(g.id)}
            title={g.name}
            onDragOver={(e) => {
              e.preventDefault();
              try {
                e.dataTransfer.dropEffect = 'copy';
              } catch (err) {
                // ignore
              }
            }}
            onDrop={(e) => {
              e.preventDefault();
              const runId =
                e.dataTransfer.getData('application/x-run-id') ||
                e.dataTransfer.getData('text/plain');
              if (!runId) return;
              handleToggleRunGroup(runId, g.id, true);
            }}
          >
            {g.name} ({groupCounts?.[g.id] ?? 0})
          </button>
        ))}
        <div className="run-groups-actions">
          <button
            type="button"
            className="run-group-chip add"
            onClick={() => setShowCreateGroup(true)}
            disabled={groupBusy}
            title="Create a group"
          >
            +
          </button>
          <button
            type="button"
            className="run-group-chip manage"
            onClick={() => setShowManageGroups(true)}
            disabled={groupBusy}
            title="Manage groups"
            aria-label="Manage groups"
          >
            <FaCog />
          </button>
        </div>
      </div>

      {/* Controls */}
      <div className="rides-controls">
        {/* Search */}
        <div className="search-box">
          <FaSearch className="search-icon" />
          <input
            type="text"
            placeholder="Search runs..."
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
          />
        </div>

        {/* Filter */}
        <div className="filter-group">
          <FaFilter className="filter-icon" />
          <select 
            value={filterBy} 
            onChange={(e) => setFilterBy(e.target.value)}
            className="filter-select"
          >
            <option value="all">All Runs</option>
            <option value="with-video">With Video</option>
            <option value="with-telemetry">With Data</option>
          </select>
        </div>

        {/* Sort */}
        <div className="filter-group">
          <FaSortAmountDown className="filter-icon" />
          <select 
            value={sortBy} 
            onChange={(e) => setSortBy(e.target.value)}
            className="filter-select"
          >
            <option value="date-desc">Newest First</option>
            <option value="date-asc">Oldest First</option>
            <option value="name">Name (A-Z)</option>
            <option value="duration-desc">Duration (Longest)</option>
            <option value="duration-asc">Duration (Shortest)</option>
            <option value="distance-desc">Distance (Longest)</option>
            <option value="distance-asc">Distance (Shortest)</option>
            <option value="tss-desc">TSS (Highest)</option>
            <option value="tss-asc">TSS (Lowest)</option>
          </select>
        </div>

        {/* View toggle */}
        <div className="view-toggle" role="group" aria-label="View mode">
          <button
            type="button"
            className={`view-toggle-btn ${viewMode === 'grid' ? 'active' : ''}`}
            onClick={() => setViewMode('grid')}
            title="Grid view"
            aria-pressed={viewMode === 'grid'}
          >
            <FaThLarge /> Grid
          </button>
          <button
            type="button"
            className={`view-toggle-btn ${viewMode === 'list' ? 'active' : ''}`}
            onClick={() => setViewMode('list')}
            title="List view"
            aria-pressed={viewMode === 'list'}
          >
            <FaThList /> List
          </button>
          <button
            type="button"
            className={`view-toggle-btn ${selectMode ? 'active' : ''}`}
            onClick={toggleSelectMode}
            title="Select runs to compare"
            aria-pressed={selectMode}
          >
            {selectMode ? <FaCheckSquare /> : <FaSquare />} Select
          </button>
        </div>
        {selectMode && selectedRunIds.size >= 2 && (
          <button
            type="button"
            className="compare-rides-button"
            onClick={() => {
              const cloudIds = Array.from(selectedRunIds).filter(
                (id) =>
                  typeof id === 'string' &&
                  !id.startsWith('local-') &&
                  !id.startsWith('queued-inline-'),
              );
              if (cloudIds.length < 2) {
                alert('Compare needs two runs that are already saved to the cloud. Pending runs cannot be compared yet.');
                return;
              }
              navigate('/compare', { state: { rideIds: cloudIds.slice(0, 2) } });
            }}
          >
            <FaChartLine /> Compare
          </button>
        )}
      </div>

      {/* Rides Grid */}
        {filteredRuns.length === 0 ? (
        <div className="empty-state">
          <div className="empty-icon">🚴</div>
            <h3>No runs found</h3>
          <p>
            {searchTerm || filterBy !== 'all' 
              ? 'Try adjusting your filters or search terms' 
              : 'Start by creating your first run!'}
          </p>
          {runs.length > 0 && filteredRuns.length === 0 && (
            <p style={{ fontSize: '14px', color: '#666' }}>
              You have {runs.length} run(s) but none match your current filters.
            </p>
          )}
          {runs.length === 0 && (
            <p style={{ fontSize: '14px', color: '#666' }}>
              No runs in database for user: {user?.email || user?.uid}
              <br />
              Check browser console for details.
            </p>
          )}
          <button className="empty-action-button" onClick={handleCreateNew}>
            <FaPlus /> Create Your First Run
          </button>
        </div>
      ) : (
        <div className={viewMode === 'list' ? 'rides-list' : 'rides-grid'}>
          {filteredRuns.map((run) => (
            <RunCard
              key={run.id}
              run={run}
              viewerUserId={user?.uid}
              viewMode={viewMode}
              onClick={() => handleRunClick(run.id)}
              onDelete={
                run.userId === user?.uid
                  ? () => setRunPendingDelete(run)
                  : undefined
              }
              onOpenGroups={
                run.userId === user?.uid && !run.pendingSync
                  ? () => setGroupPickerRun(run)
                  : undefined
              }
              isMemberRun={run.userId !== user?.uid}
              selectMode={selectMode}
              selected={selectedRunIds.has(run.id)}
              onToggleSelect={selectMode ? () => handleRunClick(run.id) : undefined}
            />
          ))}
        </div>
      )}

      <ModalPopup
        isOpen={!!runPendingDelete}
        title={DELETE_RUN_MODAL_TITLE}
        message={DELETE_RUN_MODAL_MESSAGE}
        onClose={() => setRunPendingDelete(null)}
        onCancel={() => setRunPendingDelete(null)}
        cancelLabel="Cancel"
        onConfirm={() => {
          const run = runPendingDelete;
          setRunPendingDelete(null);
          if (run) handleDeleteRun(run);
        }}
        confirmLabel="Delete"
      />

      <RunGroupPickerModal
        isOpen={!!groupPickerRun}
        title="Groups"
        runName={groupPickerRun?.runName || groupPickerRun?.name || ''}
        groups={groups}
        selectedGroupIds={groupPickerRun?.groupIds || []}
        busy={groupBusy}
        onToggleGroup={(groupId, shouldAdd) => {
          handleToggleRunGroup(groupPickerRun.id, groupId, shouldAdd);
          setGroupPickerRun((prev) => {
            if (!prev) return prev;
            const current = Array.isArray(prev.groupIds) ? prev.groupIds : [];
            const next = shouldAdd
              ? Array.from(new Set([...current, groupId]))
              : current.filter((id) => id !== groupId);
            return { ...prev, groupIds: next };
          });
        }}
        onCreateGroup={handleCreateGroup}
        onClose={() => setGroupPickerRun(null)}
      />

      <CreateRunGroupModal
        isOpen={showCreateGroup}
        busy={groupBusy}
        onClose={() => setShowCreateGroup(false)}
        onCreate={handleCreateGroup}
      />

      <ManageRunGroupsModal
        isOpen={showManageGroups}
        onClose={() => setShowManageGroups(false)}
        groups={groups}
        busy={groupBusy}
        onUpdateName={handleUpdateGroupName}
        onDelete={handleDeleteGroup}
      />
        </>
      )}
    </div>
  );
};

export default MyRides;

